import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useInventory } from '../../../contexts/InventoryContext';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import PermissionGuard from '../../Common/PermissionGuard';
import CreateRackModal from './CreateRackModal';
import AddItemToRackModal from './AddItemToRackModal';
import ItemDetailsDrawer from './ItemDetailsDrawer';
import RackOverviewTab from './RackOverviewTab';
import ItemInventoryTab from './ItemInventoryTab';

const RackManagement = () => {
  const { hasPermission } = useAuth();
  const { items: contextItems, fetchItems } = useInventory();

  // Active Tab: 'racks' or 'inventory'
  const [activeTab, setActiveTab] = useState('racks');

  // Top prominent search input (debounced)
  const [topSearch, setTopSearch] = useState('');
  const [debouncedTopSearch, setDebouncedTopSearch] = useState('');

  // Modals state
  const [isCreateRackOpen, setIsCreateRackOpen] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [addItemInitialProps, setAddItemInitialProps] = useState({});

  // Item details drawer
  const [drawerItemId, setDrawerItemId] = useState(null);

  // Locations / Warehouses
  const [warehouses, setWarehouses] = useState([]);
  const [allLocations, setAllLocations] = useState([]);

  // Summary statistics
  const [summaryStats, setSummaryStats] = useState({
    totalRacks: 0,
    totalPositions: 0,
    occupiedPositions: 0,
    availablePositions: 0,
    totalStoredQuantity: 0,
    uniqueItemsStored: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Racks data for Rack Overview tab
  const [racks, setRacks] = useState([]);
  const [racksLoading, setRacksLoading] = useState(false);

  // Item Allocations data for Item Inventory tab
  const [allocations, setAllocations] = useState([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  // Filters for Item Inventory tab
  const [storeFilter, setStoreFilter] = useState('');
  const [rackFilter, setRackFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('all');

  // Debounce top search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTopSearch(topSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [topSearch]);

  // Load locations/warehouses once on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const res = await adminApi.getLocations({ limit: 1000 });
        const list = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.locations)
            ? res.data.locations
            : Array.isArray(res.data)
              ? res.data
              : [];
        setAllLocations(list);
        const whs = list.filter(l => l.type === 'warehouse' || l.type === 'branch' || l.type === 'retail_store');
        setWarehouses(whs.length > 0 ? whs : list);
      } catch (err) {
        console.error('Failed to load locations:', err);
        setAllLocations([]);
        setWarehouses([]);
      }
    };

    loadLocations();
    if (!contextItems?.length) {
      fetchItems();
    }
  }, []);

  // Fetch summary stats
  const loadSummaryStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await inventoryApi.getRackSummaryStats({
        store: storeFilter || undefined
      });
      if (res.data) {
        setSummaryStats(res.data);
      }
    } catch (err) {
      console.error('Failed to load summary stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [storeFilter]);

  // Fetch Racks for Overview
  const loadRacks = useCallback(async () => {
    try {
      setRacksLoading(true);
      const res = await inventoryApi.getRacks({
        search: debouncedTopSearch || undefined,
        store: storeFilter || undefined,
        includeAllocations: true,
        limit: 100
      });
      setRacks(res.data || []);
    } catch (err) {
      console.error('Failed to load racks:', err);
    } finally {
      setRacksLoading(false);
    }
  }, [debouncedTopSearch, storeFilter]);

  // Fetch Item Allocations for Item Inventory
  const loadAllocations = useCallback(async () => {
    try {
      setAllocationsLoading(true);
      const res = await inventoryApi.searchItemsWithLocations({
        search: debouncedTopSearch || undefined,
        store: storeFilter || undefined,
        rack: rackFilter || undefined,
        level: levelFilter || undefined,
        stockStatus: stockStatusFilter !== 'all' ? stockStatusFilter : undefined,
        page: pagination.page,
        limit: pagination.limit
      });
      setAllocations(res.data || []);
      if (res.pagination) {
        setPagination(prev => ({
          ...prev,
          total: res.pagination.total || 0,
          pages: res.pagination.pages || 1
        }));
      }
    } catch (err) {
      console.error('Failed to load allocations:', err);
    } finally {
      setAllocationsLoading(false);
    }
  }, [
    debouncedTopSearch,
    storeFilter,
    rackFilter,
    levelFilter,
    stockStatusFilter,
    pagination.page,
    pagination.limit
  ]);

  // Reset page when search or filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedTopSearch, storeFilter, rackFilter, levelFilter, stockStatusFilter]);

  // Trigger loads based on active tab
  useEffect(() => {
    loadSummaryStats();
    if (activeTab === 'racks') {
      loadRacks();
    } else {
      loadAllocations();
    }
  }, [activeTab, loadSummaryStats, loadRacks, loadAllocations]);

  // Handle rack creation callback
  const handleRackCreated = async (rackData) => {
    await inventoryApi.createRack(rackData);
    loadRacks();
    loadSummaryStats();
  };

  // Handle rack update callback
  const handleRackUpdated = async (rackId, rackData) => {
    await inventoryApi.updateRack(rackId, rackData);
    setEditingRack(null);
    loadRacks();
    loadSummaryStats();
  };

  // Trigger edit rack
  const handleEditRack = (rack) => {
    setEditingRack(rack);
    setIsCreateRackOpen(true);
  };

  // Trigger delete rack
  const handleDeleteRack = async (rack) => {
    if (!window.confirm(`Are you sure you want to delete Rack "${rack.rackNumber}"? This cannot be undone.`)) {
      return;
    }
    try {
      await inventoryApi.deleteRack(rack._id);
      loadRacks();
      loadSummaryStats();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to delete rack');
    }
  };

  // Handle item allocation callback
  const handleItemAllocated = async (allocData) => {
    await inventoryApi.allocateRackItem(allocData);
    loadRacks();
    loadAllocations();
    loadSummaryStats();
  };

  // Handle deallocation callback
  const handleDeallocate = async (allocationId, payload) => {
    await inventoryApi.deallocateRackItem(allocationId, payload);
    loadAllocations();
    loadRacks();
    loadSummaryStats();
  };

  // Helper to open Add Item modal pre-filled with specific rack/position
  const handleOpenAddItemForPos = (target) => {
    setAddItemInitialProps(target);
    setIsAddItemOpen(true);
  };

  return (
    <PermissionGuard permission="INVENTORY:STOCK:READ">
      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Top Header & Prominent Search */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7M4 7c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3M4 7h16M7 11h10M7 15h10" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-black text-gray-900 tracking-tight">
                    Rack Management &amp; Storage Search
                  </h1>
                  <p className="text-xs text-gray-500">
                    Warehouse Layout, Level-Position Mapping &amp; Stock Allocation
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setAddItemInitialProps({});
                  setIsAddItemOpen(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-xl text-xs font-bold border border-purple-200 transition-all shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Item to Rack
              </button>

              <button
                type="button"
                onClick={() => setIsCreateRackOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm hover:shadow-indigo-200"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Rack
              </button>
            </div>
          </div>

          {/* Prominent Search Items Field */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search items by SKU, Item Name, Barcode, Rack Number or Level..."
              value={topSearch}
              onChange={(e) => setTopSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-inner"
            />
            <div className="absolute left-3.5 top-3.5 text-gray-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {topSearch && (
              <button
                onClick={() => setTopSearch('')}
                className="absolute right-3.5 top-3.5 text-gray-400 hover:text-gray-600 text-xs font-bold"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-lg">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7M4 7c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3M4 7h16M7 11h10M7 15h10" />
              </svg>
            </div>
            <div>
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Total Racks</span>
              <div className="text-2xl font-black text-gray-900 mt-0.5">
                {statsLoading ? '...' : summaryStats.totalRacks}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-lg">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            </div>
            <div>
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Storage Positions</span>
              <div className="text-2xl font-black text-gray-900 mt-0.5">
                {statsLoading ? '...' : summaryStats.totalPositions}
              </div>
              <span className="text-[10px] text-gray-400">
                {summaryStats.occupiedPositions} Occupied / {summaryStats.availablePositions} Free
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-lg">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Stored Items Units</span>
              <div className="text-2xl font-black text-emerald-800 mt-0.5">
                {statsLoading ? '...' : summaryStats.totalStoredQuantity}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-lg">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div>
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Unique SKUs on Racks</span>
              <div className="text-2xl font-black text-gray-900 mt-0.5">
                {statsLoading ? '...' : summaryStats.uniqueItemsStored}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs switcher: Rack Overview & Item Inventory */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-6">
            <button
              type="button"
              onClick={() => setActiveTab('racks')}
              className={`py-3 px-2 border-b-2 font-bold text-sm transition-all flex items-center gap-2 ${
                activeTab === 'racks'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span>Rack Overview</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 font-semibold">
                {racks.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('inventory')}
              className={`py-3 px-2 border-b-2 font-bold text-sm transition-all flex items-center gap-2 ${
                activeTab === 'inventory'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Item Inventory</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 font-semibold">
                {pagination.total}
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Contents */}
        {activeTab === 'racks' ? (
          <RackOverviewTab
            racks={racks}
            loading={racksLoading}
            warehouses={warehouses}
            onOpenAddItem={handleOpenAddItemForPos}
            onSelectItem={(itemId) => setDrawerItemId(itemId)}
            onEditRack={handleEditRack}
            onDeleteRack={handleDeleteRack}
          />
        ) : (
          <ItemInventoryTab
            allocations={allocations}
            loading={allocationsLoading}
            warehouses={warehouses}
            racks={racks}
            pagination={pagination}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            storeFilter={storeFilter}
            setStoreFilter={setStoreFilter}
            rackFilter={rackFilter}
            setRackFilter={setRackFilter}
            levelFilter={levelFilter}
            setLevelFilter={setLevelFilter}
            stockStatusFilter={stockStatusFilter}
            setStockStatusFilter={setStockStatusFilter}
            onSelectItem={(itemId) => setDrawerItemId(itemId)}
            onDeallocate={handleDeallocate}
          />
        )}

        {/* Create / Edit Rack Modal */}
        <CreateRackModal
          isOpen={isCreateRackOpen}
          onClose={() => {
            setIsCreateRackOpen(false);
            setEditingRack(null);
          }}
          warehouses={warehouses}
          onRackCreated={handleRackCreated}
          onRackUpdated={handleRackUpdated}
          editingRack={editingRack}
        />

        {/* Add Item Modal */}
        <AddItemToRackModal
          isOpen={isAddItemOpen}
          onClose={() => {
            setIsAddItemOpen(false);
            setAddItemInitialProps({});
          }}
          warehouses={warehouses}
          items={contextItems}
          initialStore={addItemInitialProps.store || ''}
          initialRackId={addItemInitialProps.rackId || ''}
          initialLevelId={addItemInitialProps.levelId || ''}
          initialPositionId={addItemInitialProps.positionId || ''}
          onItemAllocated={handleItemAllocated}
        />

        {/* Item Details Drawer Panel */}
        <ItemDetailsDrawer
          itemId={drawerItemId}
          store={storeFilter}
          warehouses={warehouses}
          onClose={() => setDrawerItemId(null)}
        />
      </div>
    </PermissionGuard>
  );
};

export default RackManagement;
